import { isPast, parseISO, isWithinInterval } from 'date-fns'
import { shortTime } from '../time'
import getEpisodeUrl from '../getEpisodeUrl'
import getCrossDomainUrl from '../getCrossDomainUrl'
import getImageRenditions from '../getImageRenditions'
import { HOST } from '../env'
import { CARD_RENDITIONS, POSTER_RENDITIONS, LOGO_RENDITIONS } from '../../constants'
import { readCookies, parseRemainingTimeString } from '../stopResume'

export function mergePromotedGroups({
  promotedGroups,
  recentlyAdded,
  mostPopular,
  personalisedRecommendations,
  newsShortform,
  hostname,
  req,
}) {
  if (!promotedGroups) return []
  return promotedGroups.map((promotedGroup) => {
    if (promotedGroup.type === 'recentlyAdded') {
      return groupFromResults({
        promotedGroup,
        hostname,
        results: recentlyAdded || promotedGroup,
      })
    }
    if (promotedGroup.type === 'mostPopular') {
      return groupFromResults({
        promotedGroup,
        hostname,
        results: mostPopular || promotedGroup,
      })
    }
    if (promotedGroup.type === 'continueWatching') {
      return groupFromContinueWatching({
        promotedGroup,
        hostname,
        req,
      })
    }
    if (promotedGroup.type === 'recommended') {
      return groupFromPersonalisedRecommendations({
        promotedGroup,
        hostname,
        req,
        results: personalisedRecommendations || promotedGroup,
      })
    }
    if (promotedGroup.type === 'news') {
      return {
        ...promotedGroup,
        assets: newsShortform,
      }
    }
    if (promotedGroup.type === 'poster') {
      return groupFromPosterResults({
        promotedGroup,
        hostname,
      })
    }
    return {
      ...promotedGroup,
      assets: remapAssets(promotedGroup.assets, assetMapping),
    }
  }).filter(Boolean)
}

function remapAssets(assets, mappingFn) {
  return Array.isArray(assets)
    ? assets
      .filter(asset => showAsset(asset, new Date(Date.now())))
      .map(mappingFn)
    : []
}

export function groupFromResults({ promotedGroup, hostname, results }) {
  return {
    ...promotedGroup,
    assets: remapAssets(
      results.results,
      episode => episodeAssetMapping({ episode, hostname })
    ),
  }
}

export function groupFromPersonalisedRecommendations({ promotedGroup, hostname, results }) {
  return {
    ...promotedGroup,
    recoID: results?.meta?.analytics?.personalisationRequestID,
    assets: remapAssets(
      results.data,
      programme => programmeAssetMapping({ programme, hostname })
    ),
  }
}

function groupFromPosterResults({ promotedGroup, hostname }) {
  return {
    ...promotedGroup,
    assets: remapAssets(
      promotedGroup.assets,
      posterAsset => posterAssetMapping({ posterAsset, hostname })
    ),
  }
}

function programmeAssetMapping({ programme, hostname }) {
  const link = getCrossDomainUrl({
    hostname,
    path: `/summary/${programme?.id}`,
  })

  const image = getImageRenditions(
    programme?.images?.[0]?._filepath,
    CARD_RENDITIONS
  )
  const webHero = getImageRenditions(
    programme?.images?.[0]?._filepath,
    ['1920x1080', '1512x851', '756x425'], // 16:9
  )
  const mobileHero = getImageRenditions(
    programme?.images?.[0]?._filepath,
    ['1000x1000', '500x500'],
  )

  return {
    id: programme.id,
    title: programme?.name,
    posterImage: null,
    subtitle: null,
    guid: programme.id,
    link,
    label: null,
    programme: null,
    overlay: null,
    image,
    webHeroImage: webHero,
    mobileHeroImage: mobileHero,
    ctaButtonLink: null,
    ctaButtonText: 'Watch now',
    ctaButtonType: 'custom',
    ctaText: programme?.description,
    playIcon: false,
    startDate: null,
    endDate: null,
    isLive: true,
    newWindow: false,
    description: programme?.description,
    hasHoverOverlay: false,
    genre: programme.genre,
    svodExclusive: false,
    forceAds: false,
    linkGuid: programme.id,
    rank: programme?.rank,
  }
}

function posterAssetMapping({ posterAsset, hostname }) {
  const link = getCrossDomainUrl({
    hostname,
    path: `/summary/${posterAsset?.assetGuid}`,
  })

  const posterImage = posterAsset?.posters?.find(poster => poster.imageType === 'PPMB') || posterAsset.image
  const titleImage = posterAsset?.posters?.find(poster => poster.imageType === 'titleImage')
  const image = getImageRenditions(
    posterImage?._filepath,
    POSTER_RENDITIONS
  )
  const titleimages = getImageRenditions(
    titleImage?._filepath,
    CARD_RENDITIONS
  )
  const assetTitleImage = { ...titleImage, _renditions: titleimages._renditions }
  const assetImage = { ...posterImage, _renditions: image._renditions }

  return {
    id: posterAsset?.assetGuid,
    title: posterAsset?.title,
    link,
    image: assetImage,
    posterImage: assetTitleImage,
    filename: posterImage?.filename,
    rank: posterAsset?.rank,
    type: posterAsset?.type,
    description: posterAsset?.shortDescription || posterAsset.shortDescription,
    hasHoverOverlay: true,
    genre: posterAsset.genre ? posterAsset.genre.name : null,
    forceAds: posterAsset.forceAds,
    sportsExclusive: posterAsset.premierSportsExclusive || false,
  }
}

export function assetMapping(asset) {
  const link = (asset.type === 'event' && asset.ctaButtonType !== 'watchLive') ? `/summary/${asset.linkGuid}` : asset._permalink

  return {
    id: asset.assetGuid,
    title: getTitle(asset),
    posterImage: asset.posterImage,
    subtitle: liveTimes(asset) || asset.subtitle,
    guid: asset.assetGuid,
    link,
    label: asset.label,
    programme: asset.programme,
    overlay: asset.overlay,
    image: asset.image,
    drmEnabled: asset.drmEnabled,
    webHeroImage: asset.webHero,
    mobileHeroImage: asset.mobileHero,
    ctaButtonLink: asset.ctaButtonLink,
    ctaButtonText: asset.ctaButtonText,
    ctaButtonType: asset.ctaButtonType,
    ctaText: asset.ctaText,
    playIcon: asset.type === 'episodes' || asset.type === 'shortform',
    startDate: asset.startDate,
    endDate: asset.endDate,
    logo: getLogo(asset),
    isLive: Boolean(asset.isLive),
    newWindow: Boolean(asset.newWindow),
    description: asset.shortDescription || asset.longDescription,
    hasHoverOverlay: hoverOverlay(asset),
    genre: asset.genre ? asset.genre.name : null,
    subGenre: asset.genre ? asset.genre?.subGenre?.name : null,
    svodExclusive: asset.svodExclusive,
    forceAds: asset.forceAds,
    linkGuid: asset.linkGuid,
    sportsExclusive: asset.premierSportsExclusive,
    autoplay: asset.autoplay,
  }
}

function episodeAssetMapping({ episode, hostname }) {
  const link = getCrossDomainUrl({
    hostname,
    path: getEpisodeUrl({
      episodeId: episode.id,
      programmeGuid: episode?.programme?.guid,
    }),
  })

  const image = getImageRenditions(
    episode?.images?.[0]?._filepath,
    CARD_RENDITIONS
  )

  return {
    id: episode.id,
    title: episode?.programme?.name,
    link,
    label: episode.genre,
    programme: episode.programme,
    overlay: null,
    subtitle: episode.title,
    image,
    playIcon: true,
    newWindow: Boolean(episode.newWindow),
    hasHoverOverlay: true,
    description: episode.summary,
    forceAds: episode?.forceAds,
    svodExclusive: episode?.svodExclusive,
  }
}

export const continueWatchingAssetMapping = ({ asset, hostname }) => {
  if (isPast(parseISO(asset.exp))) return null
  const link = getCrossDomainUrl({
    hostname,
    path: getEpisodeUrl({
      episodeId: asset.id,
      programmeGuid: asset.pid,
    }),
  })
  let image

  if (asset.img) {
    const imagePath = `https://player.api.${HOST}/v1/images/${asset.img}/rendition?redirect=true&width=[width]&height=[height]`
    image = getImageRenditions(imagePath, CARD_RENDITIONS)
  }
  const { progress, remainingdur } = getProgress(asset)

  return {
    id: asset.id,
    title: asset.prg,
    subtitle: asset.sum,
    link,
    image,
    progress,
    remainingdur,
    continueWatching: true,
    expires: asset.exp,
    forceAds: asset.fads,
    svodExclusive: asset.svodex,
    sportsExclusive: asset.sportex,
  }
}

export function groupFromContinueWatching({ promotedGroup, req, hostname }) {
  const episodes = readCookies(req)
  if (!episodes.length) {
    return null
  }
  const mappedAssets = episodes
    .map((asset => continueWatchingAssetMapping({ asset, hostname })))
    .filter(Boolean)

  return {
    ...promotedGroup,
    assets: mappedAssets,
  }
}

export function groupFromMostPopular({ promotedGroup, hostname }) {
  return {
    ...promotedGroup,
    assets: promotedGroup
      .map((asset => assetMapping({ asset, hostname })))
      .filter(Boolean),
  }
}

export function mapFromSpecialEvent({ isSpecialEvent }) {
  // eslint-disable-next-line array-callback-return
  isSpecialEvent.assets.map((asset) => {
    const image = getImageRenditions(
      asset?.image?._filepath,
      ['2752x600', '1376x300'],
    )
    // eslint-disable-next-line no-param-reassign
    asset.image._renditions = image._renditions

    const webHero = getImageRenditions(
      asset?.webHero?._filepath,
      ['1920x1080', '1512x851', '756x425'], // 16:9
    )
    if (webHero?._renditions) {
      // eslint-disable-next-line no-param-reassign
      asset.webHero._renditions = webHero?._renditions
    }

    const mobileHero = getImageRenditions(
      asset?.mobileHero?._filepath,
      ['1000x1000', '500x500'],
    )
    if (mobileHero?._renditions) {
      // eslint-disable-next-line no-param-reassign
      asset.mobileHero._renditions = mobileHero?._renditions
    }
  })
}

function getProgress(asset) {
  const { dur, pos } = asset
  const progress = pos / dur
  const remainingdur = progress ? `${parseRemainingTimeString(dur - pos)} left` : null
  return { progress, remainingdur } || { progress: 0, remainingdur: '' }
}

function getTitle(asset) {
  if (asset.type === 'live') {
    const liveTitle = asset?._extraInformation?.now?.title
    if (liveTitle) return liveTitle
  }
  return asset.title
}

function getLogo(asset) {
  if (asset.type === 'live') {
    const liveLogo = getImageRenditions(asset?.logo?._filepath, LOGO_RENDITIONS)
    if (liveLogo) return liveLogo
  }
  return null
}

function liveTimes(asset) {
  if (asset.type === 'live' && asset._extraInformation) {
    const now = asset?._extraInformation?.now?.startTime
    const next = asset?._extraInformation?.now?.endTime
    return (now && next) ? `${shortTime(now)} - ${shortTime(next)}` : null
  }
  return null
}

const shouldFilter = (asset) => {
  const hasIsLive = typeof asset.isLive !== 'undefined'
  const hasStartDate = typeof asset.startDate === 'string'
    && asset.startDate !== '0000-00-00 00:00:00'
  const hasEndDate = typeof asset.endDate === 'string'
    && asset.endDate !== '0000-00-00 00:00:00'
  return hasIsLive && hasStartDate && hasEndDate
}

const showAsset = (asset, point) => {
  // if any of these things aren't set, include as usual
  if (!shouldFilter(asset)) return true
  // otherwise test if point is in range
  // and invert the result if isLive is false
  const inRange = isWithinInterval(point, {
    start: parseISO(asset.startDate),
    end: parseISO(asset.endDate),
  })
  return asset.isLive ? inRange : !inRange
}

const hoverOverlay = (asset) => {
  const validTypes = ['episodes', 'programmes']
  return (validTypes.includes(asset.type) && (!!asset.shortDescription || !!asset.longDescription))
}
